import { AUTO_TRIGGER_LOCK, GATEOFF, MANDATE } from "../../constants/common/locationFieldConstants";
import { capitalizeFirstLetter } from "../pdp/common";
import CookieUtil from "../storage/cookieUtil";
import {
  LAST_VISITED_ACCURACY_LEVEL,
  LAST_VISITED_ACCURACY_TYPE,
  LAST_VISITED_ADDRESS,
  LAST_VISITED_ADDRESS_LAT_LONG,
  LAST_VISITED_PINCODE,
  LAST_VISITED_PLACE_TYPES,
} from "../../constants/storage";
import {
  getAccuracyLevelForTypes,
  getAddressServiceabilityLocation,
  getLastVisitedAccuracyLevel,
} from "../checkout/deliveryAddress";
import { getIconUrl } from "../common";
import { getCleverTapPageType } from "../fnpPageType";

/**
 * Function to get filtered city list
 *
 * @param {string} value value of input.
 * @param {Array} cityListData citylist
 * @returns {Array} filtered list of cities.
 */
export const getFilteredCitiesList = (value, cityListData, cityNameKey = "CITY_NAME") => {
  let filteredCitiesInclude = [];
  if (!value) {
    return filteredCitiesInclude;
  }
  const filteredCitiesStartsWith = cityListData?.filter((city) =>
    city?.[cityNameKey]?.toLowerCase().startsWith(value.toLowerCase()),
  );

  if (filteredCitiesStartsWith?.length === 0) {
    filteredCitiesInclude = cityListData?.filter((city) =>
      city?.[cityNameKey]?.toLowerCase().includes(value.toLowerCase()),
    );
  }
  return [...filteredCitiesStartsWith, ...filteredCitiesInclude].reduce((unique, cityObj) => {
    if (!unique.some((obj) => obj.cityId === cityObj.cityId && obj[cityNameKey] === cityObj[cityNameKey])) {
      unique.push(cityObj);
    }
    return unique;
  }, []);
};

/**
 * Return the gate config based on A/B test value.
 * @param {*} configValue
 * @returns
 */
export const getGateConfig = (configValue, cmsConfigData) => {
  let isAuto = configValue !== GATEOFF;
  let isMandate = configValue === MANDATE;
  const mandatePLPList = cmsConfigData?.mandateGatePLPList || [];
  const isCurrentPLPAvailableForMandate = mandatePLPList?.length
    ? mandatePLPList?.some((plp) => window.location.pathname.includes(plp))
    : false;

  let configObj = {
    ...cmsConfigData,
    isAutoGateEnabledOnDesktopPLP:
      isCurrentPLPAvailableForMandate || (configValue ? isAuto : cmsConfigData?.isAutoGateEnabledOnDesktopPLP),
    isAutoGateMandateOnDesktopPLP:
      isCurrentPLPAvailableForMandate || (configValue ? isMandate : cmsConfigData?.isAutoGateMandateOnDesktopPLP),
    isAutoGateEnabledOnMobilePLP:
      isCurrentPLPAvailableForMandate || (configValue ? isAuto : cmsConfigData?.isAutoGateEnabledOnMobilePLP),
    isAutoGateMandateOnMobilePLP:
      isCurrentPLPAvailableForMandate || (configValue ? isMandate : cmsConfigData?.isAutoGateMandateOnMobilePLP),
  };
  return configObj;
};

/**
 * Return the gate auto trigger flag on the basis of plp list.
 * @param {*} mandateGatePLPList
 * @returns {boolean} isAutoTriggerApplicable
 */
export const getGateAutoTriggerFlag = (mandateGatePLPList) => {
  if (mandateGatePLPList === undefined) {
    return false;
  }
  const mandatePLPList = mandateGatePLPList || [];
  const isCurrentPLPAvailableForMandate = mandatePLPList?.length
    ? mandatePLPList?.some((plp) => window.location.pathname.includes(plp))
    : false;

  const isAutoTriggerApplicable = isCurrentPLPAvailableForMandate ? false : CookieUtil.getCookie(AUTO_TRIGGER_LOCK);
  return !!isAutoTriggerApplicable;
};

/**
 * returns the country flags for location lock
 */
export const fetchCountryFlag = (countryListData = [], catalogId) => {
  if (Array.isArray(countryListData)) {
    const entry = countryListData?.find((obj) => obj.catelogID === catalogId);
    return entry ? entry.webLogo : getIconUrl("/icons/location_icon_green.svg");
  }
  return getIconUrl("/icons/location_icon_green.svg");
};

/**
 * Capitalizes the first letter of each word in a string.
 * @param {string} str - Input string.
 * @returns {string}
 */
export const capitalizeWords = (str) => {
  if (!str) {
    return str;
  }
  return str.split(" ").map(capitalizeFirstLetter).join(" ");
};

/**
 * Returns a comma separated string of indexes from 0 to totalNoOfItems - 1.
 * @param {number} totalNoOfItems - Total number of items.
 * @returns {string} A comma separated string of indexes from 0 to totalNoOfItems - 1.
 */
export const commasSeparatedIndexes = (totalNoOfItems) => {
  return Array.from({ length: totalNoOfItems }, (_, index) => index).join(",");
};

/**
 * CleverTap payload for Location Popup Viewed / Closed.
 * Call-site fields below; ctid, login_status, platform, analytics_sessionid,
 * browsing_city, and browsing_country are attached by useAnalytics / useCleverTap.
 *
 * @param {Object} params
 * @returns {Object}
 */
export const buildLocationPopupViewedData = ({
  page_name = "",
  page_type = "",
  is_location_available = false,
  mandatory = false,
  auto = false,
  pincode = "",
  area = "",
  system_pincode = "",
  country = "",
  city = "",
  is_bottom_sheet = false,
  is_saved_address_available = false,
  user_initiated = false,
  clean_url = "",
  complete_url = "",
  pid = "",
  sku_id = "",
  flow = "new",
  entry_accuracy = "",
  is_location_retained,
} = {}) => ({
  page_name,
  page_type: getCleverTapPageType(page_type),
  is_location_available: Boolean(is_location_available),
  mandatory: Boolean(mandatory),
  auto: Boolean(auto),
  pincode: pincode || "",
  area: area || "",
  system_pincode: system_pincode || pincode || "",
  country: country || "",
  city: city || "",
  is_bottom_sheet: Boolean(is_bottom_sheet),
  is_saved_address_available: Boolean(is_saved_address_available),
  user_initiated: Boolean(user_initiated),
  clean_url: clean_url || "",
  complete_url: complete_url || "",
  pid: pid || "",
  sku_id: sku_id || "",
  flow,
  entry_accuracy: entry_accuracy || "",
  ...(typeof is_location_retained === "boolean" ? { is_location_retained } : {}),
});

const LAT_LONG_COOKIE_OPTIONS = { maxAge: 60 * 60 * 24 * 7, path: "/", encode: decodeURIComponent };

/**
 * Stores the latest selected / added saved address's lat/long and accuracy level; drops stale ones when unknown.
 * @param {number|string} [lat]
 * @param {number|string} [long]
 */
export const setLastVisitedLatLong = (lat, long, accuracyType = "") => {
  if (Number(lat) && Number(long)) {
    CookieUtil.setCookie(LAST_VISITED_ADDRESS_LAT_LONG, `${Number(lat)},${Number(long)}`, LAT_LONG_COOKIE_OPTIONS);
  } else {
    CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, { path: "/" });
  }
  // Saved address accuracy level (level, or a Google type mapped to one) for the serviceability APIs.
  const level = getAddressServiceabilityLocation({ lat, long, accuracyType })?.accuracyType;
  if (level) {
    CookieUtil.setCookie(LAST_VISITED_ACCURACY_LEVEL, level, LAT_LONG_COOKIE_OPTIONS);
  } else {
    CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_LEVEL, { path: "/" });
  }
};

const LOCATION_ACCURACY_LABELS = ["high", "medium", "low", "pincode"];

/**
 * @param {string} [accuracyType]
 * @returns {string} "high" | "medium" | "low" | "pincode" | ""
 */
const resolveLocationAccuracyLabel = (accuracyType = "") => {
  const accuracy = String(accuracyType || "").trim();
  if (!accuracy || accuracy.toLowerCase() === "null") return "";
  const normalized = accuracy.toLowerCase();
  if (LOCATION_ACCURACY_LABELS.includes(normalized)) return normalized;
  return getAccuracyLevelForTypes([accuracy]);
};

/**
 * Google autocomplete keeps place types; a pincode pick clears them.
 * @returns {"gate_suggestion"|"pincode"}
 */
export const getConfirmedLocationSource = () => {
  const placeTypes = String(CookieUtil.getCookie(LAST_VISITED_PLACE_TYPES) || "").trim();
  return placeTypes ? "gate_suggestion" : "pincode";
};

/**
 * Extra CleverTap fields for Location Saved.
 * accuracy_type is only included for a Google autocomplete selection.
 * @param {"gate_suggestion"|"pincode"|"saved_address"} locationSource
 * @param {string} [savedAddressAccuracyType]
 * @returns {{ location_accuracy: string, location_source: string, accuracy_type?: string }}
 */
export const getLocationSavedTrackingFields = (locationSource, savedAddressAccuracyType = "") => {
  if (locationSource === "saved_address") {
    return {
      location_accuracy: resolveLocationAccuracyLabel(savedAddressAccuracyType) || "pincode",
      location_source: "saved_address",
    };
  }
  if (locationSource === "pincode") {
    return {
      location_accuracy: "pincode",
      location_source: "pincode",
    };
  }
  const locationAccuracy =
    String(CookieUtil.getCookie(LAST_VISITED_ACCURACY_LEVEL) || "").trim() || getLastVisitedAccuracyLevel();
  return {
    location_accuracy: locationAccuracy,
    accuracy_type: String(CookieUtil.getCookie(LAST_VISITED_ACCURACY_TYPE) || "").trim(),
    location_source: "gate_suggestion",
  };
};

const ENTRY_ACCURACY_LEVELS = ["high", "medium", "low"];

/**
 * Accuracy of the location already selected when a location popup opens.
 * A pincode pick has no stored accuracy level, so it is reported as "pincode".
 * @returns {"high"|"medium"|"low"|"pincode"|""}
 */
export const getEntryAccuracy = () => {
  const storedLevel = String(CookieUtil.getCookie(LAST_VISITED_ACCURACY_LEVEL) || "")
    .trim()
    .toLowerCase();
  if (ENTRY_ACCURACY_LEVELS.includes(storedLevel)) return storedLevel;
  const resolvedLevel = String(getLastVisitedAccuracyLevel() || "")
    .trim()
    .toLowerCase();
  if (ENTRY_ACCURACY_LEVELS.includes(resolvedLevel)) return resolvedLevel;
  const hasEarlierLocation = Boolean(
    String(CookieUtil.getCookie(LAST_VISITED_PINCODE) || "").trim() ||
    String(CookieUtil.getCookie(LAST_VISITED_ADDRESS) || "").trim(),
  );
  return hasEarlierLocation ? "pincode" : "";
};

/**
 * Identity of the location selected when the popup opened, for the closed event.
 * @param {string} [pincode]
 * @param {string} [area]
 * @returns {string}
 */
export const getLocationSelectionKey = (pincode = "", area = "") => {
  const pin = String(pincode || "").trim();
  const place = String(area || "").trim();
  return pin || place ? `${pin}|${place}` : "";
};
